// Tipos para gestión extendida de comercios en el frontend

export interface Departamento {
  id: string;
  nombre: string;
}

export interface Provincia {
  id: string;
  departamento_id: string;
  nombre: string;
}

export interface Distrito {
  id: string;
  provincia_id: string;
  nombre: string;
}

export interface Direccion {
  id?: number;
  calle: string;
  numero?: string;
  departamento_id: string;
  provincia_id: string;
  distrito_id: string;
  referencia?: string;
  // Datos desnormalizados para mostrar
  departamento_nombre?: string;
  provincia_nombre?: string;
  distrito_nombre?: string;
}

export interface HorarioDia {
  abierto: boolean;
  hora_apertura?: string; // formato "08:00"
  hora_cierre?: string;   // formato "18:00"
}

export interface Horario {
  lunes: HorarioDia;
  martes: HorarioDia;
  miercoles: HorarioDia;
  jueves: HorarioDia;
  viernes: HorarioDia;
  sabado: HorarioDia;
  domingo: HorarioDia;
}

export interface ComercioArchivo {
  id: number;
  comercio_id: number;
  tipo: 'terminos_condiciones' | 'documento' | 'imagen';
  nombre_original: string;
  ruta_archivo: string;
  tamaño: number;
  created_at: string;
}

export interface Local {
  id: number;
  comercio_id: number;
  nombre: string;
  direccion: Direccion;
  hereda_horario: boolean;
  horario_propio?: Horario;
  activo: boolean;
  created_at: string;
  updated_at?: string;
}

export interface LocalInput {
  nombre: string;
  direccion: Omit<Direccion, 'id'>;
  hereda_horario: boolean;
  horario_propio?: Horario;
}

// Evolución de Convenio → Campaña
export interface Campaña {
  id: number;
  comercio_id: number;
  nombre: string;
  descripcion: string;
  fecha_inicio: string;
  fecha_fin: string;
  aplica_todo_negocio: boolean;
  locales_aplicables: number[]; // IDs de locales si no aplica a todo
  
  // Configuración de descuentos
  consumo_minimo?: number;
  descuento_maximo?: number;
  porcentaje_descuento?: number;
  monto_descuento?: number;
  numero_promociones: number; // límite total de usos
  promociones_usadas: number;
  
  // Control
  activo: boolean;
  created_at: string;
  updated_at?: string;
  
  // Datos relacionados para display
  locales_nombres?: string[];
  comercio_nombre?: string;
}

export interface CampañaInput {
  nombre: string;
  descripcion: string;
  fecha_inicio: string;
  fecha_fin: string;
  aplica_todo_negocio: boolean;
  locales_aplicables?: number[];
  
  // Configuración de descuentos
  consumo_minimo?: number;
  descuento_maximo?: number;
  porcentaje_descuento?: number;
  monto_descuento?: number;
  numero_promociones: number;
}

// Comercio extendido
export interface ComercioExtendido {
  // Datos básicos (heredados)
  id: number;
  ruc: string;
  razon_social: string;
  email: string;
  telefono?: string;
  activo: boolean;
  tipo: 'comercio' | 'admin';
  created_at: string;
  updated_at?: string;
  
  // Nuevos datos extendidos
  direccion_principal: Direccion;
  horario_atencion: Horario;
  
  // Relaciones
  locales: Local[];
  campañas: Campaña[];
  archivos: ComercioArchivo[];
  
  // Estadísticas calculadas
  total_locales?: number;
  campañas_activas?: number;
  total_redenciones?: number;
}

export interface ComercioExtendidoInput {
  // Datos básicos
  ruc: string;
  razon_social: string;
  email: string;
  telefono?: string;
  password?: string; // opcional para edición
  
  // Dirección principal
  direccion_principal: Omit<Direccion, 'id'>;
  
  // Horario de atención
  horario_atencion: Horario;
}

// Filtros para búsquedas
export interface ComercioFilters {
  buscar?: string; // busca en RUC, razón social, email
  departamento_id?: string;
  provincia_id?: string;
  distrito_id?: string;
  activo?: boolean;
  con_locales?: boolean;
  con_campañas_activas?: boolean;
}

export interface LocalFilters {
  comercio_id?: number;
  activo?: boolean;
  departamento_id?: string;
  provincia_id?: string;
  distrito_id?: string;
}

export interface CampañaFilters {
  comercio_id?: number;
  activo?: boolean;
  vigente?: boolean; // fecha actual entre inicio y fin
  local_id?: number;
  aplica_todo_negocio?: boolean;
}

// Paginación
export interface PaginationParams {
  page: number;
  limit: number;
  sort_by?: string;
  sort_order?: 'ASC' | 'DESC';
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_items: number;
    items_per_page: number;
  };
}

// Utilidades para horarios
export const DIAS_SEMANA = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'] as const;
export type DiaSemana = typeof DIAS_SEMANA[number];

export const HORARIO_DEFAULT: Horario = {
  lunes: { abierto: true, hora_apertura: '08:00', hora_cierre: '18:00' },
  martes: { abierto: true, hora_apertura: '08:00', hora_cierre: '18:00' },
  miercoles: { abierto: true, hora_apertura: '08:00', hora_cierre: '18:00' },
  jueves: { abierto: true, hora_apertura: '08:00', hora_cierre: '18:00' },
  viernes: { abierto: true, hora_apertura: '08:00', hora_cierre: '18:00' },
  sabado: { abierto: true, hora_apertura: '08:00', hora_cierre: '14:00' },
  domingo: { abierto: false }
};

// Estados de formularios
export interface FormError {
  field: string;
  message: string;
}

export interface ComercioFormState {
  loading: boolean;
  errors: FormError[];
  success: boolean;
  message?: string;
}

// Para dropdowns y selectors
export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

// Respuestas del API
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  errors?: FormError[];
}
